// Shared inline-SVG icon set for tables and toolbars. The app deliberately uses no
// icon dependency (no lucide); before this file every table hand-rolled its own SVG
// paths and its own sort/chevron glyphs, so no two matched. Route table chrome icons
// through here so the stroke weight, viewBox and glyph shape stay consistent. Each
// icon inherits colour via `currentColor` and takes a `className` for sizing/colour.

type IconProps = { className?: string };

export function ChevronLeftIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M10 3L5 8l5 5" />
    </svg>
  );
}

export function ChevronRightIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M6 3l5 5-5 5" />
    </svg>
  );
}

export function ChevronDownIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3 6l5 5 5-5" />
    </svg>
  );
}

export function SearchIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10 10l3 3" strokeLinecap="round" />
    </svg>
  );
}

// Three descending lines — the "filter" affordance.
export function FilterIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path d="M2 4h12M4 8h8M6 12h4" strokeLinecap="round" />
    </svg>
  );
}

// Three even lines — the "sort" affordance.
export function SortLinesIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path d="M3 4h10M5 8h6M7 12h2" strokeLinecap="round" />
    </svg>
  );
}

export function PlusIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className}>
      <path d="M6 1v10M1 6h10" />
    </svg>
  );
}

// The single close glyph. Replaces the app's scattered literal x/times characters and
// per-component inline close SVGs so every dismiss control matches.
export function CloseIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className={className}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

// Check mark for the approve half of approve/deny pairs (paired with CloseIcon for deny).
export function CheckIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  );
}

// Download affordance for file/document download links.
export function DownloadIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M8 2.5v7M5 7l3 3 3-3M3 13h10" />
    </svg>
  );
}

// Diagonal arrow out of the corner - the "export" affordance (org chart PDF, leave CSV).
export function ExportIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M7 17L17 7M17 7H9M17 7v8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Two overlapping sheets — the "copy" affordance.
export function CopyIcon({ className }: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 5.5V4A1.5 1.5 0 0 0 9 2.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5" />
    </svg>
  );
}

// Column-sort indicator: a faint up/down pair when the column is inactive, and a
// single brand-coloured triangle pointing the sorted direction when active.
export function SortGlyph({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) {
    return (
      <svg aria-hidden="true" viewBox="0 0 10 12" fill="currentColor" className="h-3 w-2.5 text-slate-300">
        <path d="M5 1l3 4H2zM5 11L2 7h6z" />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" viewBox="0 0 10 10" fill="currentColor" className="h-2.5 w-2.5 text-brand-hover">
      <path d={dir === 'asc' ? 'M5 2l4 6H1z' : 'M5 8L1 2h8z'} />
    </svg>
  );
}
